import { useAuthenticationStore } from '@/stores/authentication/AuthenticationStore';
import { useExhibitorStore } from '@/stores/exhibitor/ExhibitorStore';
import { getWhiteLabelState } from '@/stores/storeFacade';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { enrichListing } from '@/utils/listingUtils';
import type { ArcualInventoryItem, ListingFilterOptions, ListingFilters, ResultList } from '@arcual/lib-app-platform';
import { getListingsService } from '@arcual/lib-app-platform';
import { PagedListCache } from '@/model/common/PagedListCache';

const listingStoreName = 'listingStore';
export const useListingStore = defineStore(listingStoreName, () => {
  // used for caching listings page view for navigation back to the same state.
  const pagedListingsCache = ref<Record<string, PagedListCache<ArcualInventoryItem>>>({});

  const listings = ref<Record<string, ArcualInventoryItem>>({});
  const randomSeed = ref<string>(Math.round(Math.random() * Number.MAX_SAFE_INTEGER).toString());
  const activationId = computed(() => getWhiteLabelState().activationId.value);

  const getPreviousItemId = (id: string): string => {
    const artworkKeys = Object.keys(listings.value);
    const index = artworkKeys.findIndex(artworkKey => artworkKey === id);
    if (index - 1 >= 0) {
      return artworkKeys[index - 1];
    }

    return '';
  };

  const getNextItemId = (id: string): string => {
    const artworkKeys = Object.keys(listings.value);
    const index = artworkKeys.findIndex(artworkKey => artworkKey === id);
    if (index + 1 < artworkKeys.length) {
      return artworkKeys[index + 1];
    }

    return '';
  };

  const getListing = async (id: string): Promise<ArcualInventoryItem> => {
    let listing = listings.value[id];

    if (listing) {
      return listing;
    }

    listing = await getListingsService().getListing(
      activationId.value,
      id,
      !!useAuthenticationStore().getGuestEmail().value,
    );
    await enrichListing(listing);

    listings.value[id] = listing;
    return listing;
  };

  const nextListingsPage = async (
    contextKey: string,
    filters?: ListingFilters,
    includeSeller = true,
    reset = false,
  ): Promise<PagedListCache<ArcualInventoryItem>> => {

    let cache = pagedListingsCache.value[contextKey];

    if (!cache) {
      cache = new PagedListCache<ArcualInventoryItem>();
      pagedListingsCache.value[contextKey] = cache;
    }

    if (reset) {
      cache.reset();
    }

    if (cache.total === 0 || cache.count() < cache.total) {
      cache.page += 1;

      const result = await getListingsService().postListings(activationId.value, {
        ...filters,
        page: cache.page,
        randomSeed: randomSeed.value,
      });

      cache.total = result.total;

      const listingPage = [];

      for (const listing of result.items) {
        if (includeSeller) {
          listing.seller = (await useExhibitorStore().getExhibitor(listing.sellerProfileId)).value;
        }
        listings.value[listing.id] = listing;
        listingPage.push(listing);
      }

      if (listingPage.length > 0) {
        cache.pages.push(listingPage);
      }
    }

    return cache;
  };

  const postListings = async (
    filters?: ListingFilters,
    includeSeller = true,
  ): Promise<ResultList<ArcualInventoryItem>> => {

    const result = await getListingsService().postListings(activationId.value, {
      ...filters,
      randomSeed: randomSeed.value,
    });

    for (const listing of result.items) {
      if (includeSeller) {
        listing.seller = (await useExhibitorStore().getExhibitor(listing.sellerProfileId)).value;
      }
      listings.value[listing.id] = listing;
    }

    return result;
  };

  const getListingsFilters = async (filters?: Partial<ListingFilters>): Promise<ListingFilterOptions> => {
    return getListingsService().getListingsFilters(activationId.value, filters);
  };

  /**
   * Reset the store to its initial state
   */
  const reset = () => {
    listings.value = {};
    randomSeed.value = Date.now().toString();
  };

  return {
    getListing,
    postListings,
    nextListingsPage,
    getListingsFilters,
    getNextItemId,
    getPreviousItemId,
    reset,
  };
});
